import * as React from 'react';
import { isNil, isArray } from 'lodash/fp';
import { isEqual, isFunction, isString } from 'lodash';
import { Box, Chip, CircularProgress, FormGroup, PopperProps, Popper, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { classes } from 'typestyle';
import { Close, KeyboardArrowDown } from '@material-ui/icons';

import styles, {
  autocompleteOptionStyles,
  autocompleteStyles,
  chipStyles,
  customPopperStyle,
  generalPopper,
  generalPopperHeight,
} from './InputSuggest.styles';
import VirtualizedListbox from 'src/common-ui/components/VirtualizedListbox/VirtualizedListbox';

const ErrorTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: 'rgba(247, 92, 105, 0.97)',
    color: '#FFF',
    boxShadow: theme.shadows[1],
    fontSize: 14,
  },
}))(Tooltip);

export const ErrorIcon = (props: Suggestion) => {
  return props.icon ? (
    <ErrorTooltip title="Conflict: Current value not found in valid values.">
      <i className={classes(styles.errorIcon, props.icon)} />
    </ErrorTooltip>
  ) : null;
};

const PopperWidthOverride = function (props: PopperProps) {
  let newWidth = props.style?.width;
  if (props.anchorEl != null) {
    const el = isFunction(props.anchorEl) ? props.anchorEl() : props.anchorEl;
    newWidth = el.clientWidth;
  }
  return <Popper {...props} style={{ ...props.style, width: newWidth }} />;
};

interface InputSuggestProps {
  validValues: Suggestion[];
  selected: Suggestion[] | Suggestion | string | undefined;
  multiSelect?: boolean;
  valid?: boolean;
  placeholder?: string;
  onSelect?: (sugg: Suggestion) => void;
  onMultiSelect?: (suggs: Suggestion[]) => void;
  dataQa?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  defaultOpenOnFocus?: boolean;
  /** **For lazy loading** */
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  allowPortal?: boolean;
}

export interface Suggestion {
  label: string;
  value: string | number | object;
  icon?: string;
}
export default class InputSuggest extends React.Component<InputSuggestProps> {
  static defaultProps = {
    multiSelect: false,
  };

  private inputElement: HTMLInputElement | null = null;
  private BoxRef = React.createRef<HTMLDivElement>();

  componentDidUpdate(prevProps: InputSuggestProps) {
    // when dropdown just opened AND it’s not loading yet…
    const justOpened = !prevProps.open && this.props.open;
    const loadFinished = prevProps.isLoading && !this.props.isLoading && this.props.open;

    if (justOpened || loadFinished) {
      // this is required for selection of text in style edit section, see INT-5048
      // give React one tick to actually mount the <input>
      setTimeout(() => {
        if (this.inputElement) {
          this.inputElement.focus();
          this.inputElement.select();
        }
      }, 0);
    }
  }

  shouldComponentUpdate(nextProps: Readonly<InputSuggestProps>): boolean {
    return !isEqual(nextProps, this.props);
  }

  getSelection() {
    const { selected } = this.props;
    if (isNil(selected)) return [];
    if (isString(selected)) {
      return [
        {
          label: selected,
          value: selected,
          icon: 'fal fa-exclamation-triangle',
        },
      ];
    }
    if (!isArray(selected) && selected.value === '') {
      return [];
    }
    return isArray(selected) ? selected : [selected];
  }

  handleSingleSelect(selection: Suggestion | null) {
    const { onSelect } = this.props;
    if (selection && onSelect) {
      onSelect(selection);
    }
  }

  handleMultiSelect(selections: Suggestion[]) {
    const { onMultiSelect } = this.props;
    if (onMultiSelect) {
      onMultiSelect(selections);
    }
  }

  render() {
    const {
      validValues,
      multiSelect,
      placeholder,
      dataQa,
      disabled,
      isLoading,
      onBlur,
      onFocus,
      open,
      onOpen,
      onClose,
      defaultOpenOnFocus,
      allowPortal = false,
    } = this.props;

    const selected = this.getSelection();
    const drawDownSizingClass = allowPortal ? '' : customPopperStyle;
    return (
      <FormGroup className={styles.container}>
        <div className={classes('select-container', disabled ? 'disabled' : '')} ref={this.BoxRef}>
          <Box className="input-suggest-container" data-qa={dataQa}>
            <Autocomplete
              freeSolo={multiSelect}
              forcePopupIcon
              selectOnFocus
              disablePortal={!allowPortal}
              autoFocus
              filterSelectedOptions={multiSelect}
              disableCloseOnSelect={multiSelect}
              openOnFocus={multiSelect || defaultOpenOnFocus}
              multiple={multiSelect}
              disabled={disabled}
              loading={isLoading}
              options={validValues}
              open={open}
              PopperComponent={(props: PopperProps) => {
                // `ag-custom-component-popup` allows custom popups from editors (dropdown options) to properly notify ag-grid when to stop editing
                const maybeModifiedClassName = classes(
                  props.className ?? '',
                  allowPortal ? 'ag-custom-component-popup' : '',
                );
                return (
                  <PopperWidthOverride {...props} className={maybeModifiedClassName} anchorEl={this.BoxRef.current} />
                );
              }}
              onOpen={onOpen}
              onClose={onClose}
              popupIcon={isLoading ? <CircularProgress color="inherit" size={20} /> : <KeyboardArrowDown />}
              classes={{
                ...autocompleteStyles(!!multiSelect),
                popper: drawDownSizingClass,
                paper: generalPopper,
                option: autocompleteOptionStyles,
              }}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.value === value.value}
              value={multiSelect ? selected : selected[0] || null}
              onChange={(_evt, newValue) => {
                if (!multiSelect) {
                  this.handleSingleSelect((newValue as Suggestion) || null);
                } else {
                  this.handleMultiSelect((newValue as Suggestion[]) || []);
                }
              }}
              loadingText={<CircularProgress color="inherit" size={20} />}
              renderOption={(option) => <div data-qa="option-value">{option.label || option.value}</div>}
              renderTags={(value: Suggestion[], getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    classes={{ ...chipStyles }}
                    deleteIcon={<Close />}
                    key={index}
                    label={option.label}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              ListboxComponent={(listboxProps) => {
                // Only auto-highlight the first option when it's not multiSelect AND the selected option has empty value
                const shouldAutoHighlight = !multiSelect && (selected.length === 0 || selected[0]?.value === '');

                // Find the index of the selected option in the validValues array
                const selectedIndex =
                  !multiSelect && selected.length > 0 && selected[0]?.value !== ''
                    ? validValues.findIndex((option) => option.value === selected[0].value)
                    : -1;

                return (
                  <VirtualizedListbox
                    {...listboxProps}
                    maxListHeight={generalPopperHeight}
                    autoHighlight={shouldAutoHighlight}
                    highlightedIndex={shouldAutoHighlight ? 0 : selectedIndex}
                  />
                );
              }}
              renderInput={(params) => {
                const endAdornmentChildren = React.Children.toArray(
                  React.isValidElement(params.InputProps.endAdornment)
                    ? params.InputProps.endAdornment.props.children
                    : [],
                );

                return (
                  <TextField
                    {...params}
                    placeholder={isLoading ? 'Loading...' : (placeholder ?? 'Search...')}
                    variant="outlined"
                    style={{ backgroundColor: disabled ? 'rgb(242, 242, 242)' : undefined }}
                    onBlur={onBlur}
                    onFocus={(e) => {
                      // this is required for selection of text in ag-grid use, see INT-5048
                      e.target.select();
                      if (onFocus) {
                        onFocus();
                      }
                      const el = this.inputElement;
                      if (el) {
                        el.scrollLeft = 0;
                        const length = el.value.length;
                        el.setSelectionRange(0, length);
                      }
                    }}
                    value={multiSelect ? selected : selected[0] || null}
                    data-qa="selected-option"
                    inputRef={(node) => {
                      // This allows for the input to properly focus onClick,
                      // it's assigning the inputRef we instantiate above
                      // without it - focus & clickaway do not work
                      this.inputElement = node;
                      if (params.InputProps.ref) {
                        (params.InputProps.ref as React.RefCallback<HTMLInputElement>)(node);
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>{multiSelect ? params.InputProps.startAdornment : <ErrorIcon {...selected[0]} />}</>
                      ),
                      endAdornment: (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            pr: 1,
                          }}
                        >
                          {endAdornmentChildren.length > 0 &&
                            endAdornmentChildren.map((child, index) => (
                              <React.Fragment key={index}>
                                {((endAdornmentChildren.length === 1 && index === 0) ||
                                  (endAdornmentChildren.length > 1 && index > 0)) && (
                                  <Box
                                    width="1px"
                                    height={multiSelect ? '85%' : '50%'}
                                    mx={0.5}
                                    bgcolor="rgba(98, 95, 95, 0.2)"
                                    alignSelf="center"
                                  />
                                )}
                                {child}
                              </React.Fragment>
                            ))}
                        </Box>
                      ),
                    }}
                  />
                );
              }}
            />
          </Box>
        </div>
      </FormGroup>
    );
  }
}
